/**
 * Copyright 2025 Design Barn Inc.
 */

'use client';

import { ModalContent, ModalOverlay } from '@lottiefiles/ds-core';
import { Root as ModalRoot, Portal as ModalPortal } from '@radix-ui/react-dialog';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';

import { ErrorBoundary, Toast } from '../components';
import { FeatureFlagProvider } from '../contexts/feature-flags-context';
import { useIframeMessaging } from '../contexts/iframe-messaging-context';

export default function Layout({ children }: { children: React.ReactNode }): React.ReactElement {
  const { closeModal } = useIframeMessaging();
  const [showToast, setShowToast] = useState<{ message: string; show: boolean }>({
    show: false,
    message: 'Password reset instructions sent to your email if it has already been registered.',
  });

  const onOpenChangeModal = useCallback(
    (open: boolean) => {
      if (!open) {
        closeModal({ userInitiated: true });
      }
    },
    [closeModal],
  );

  const searchParams = useSearchParams();
  const messageParams = searchParams.get('message');

  useEffect(() => {
    if (messageParams === 'reset-password-success') {
      setShowToast({ ...showToast, message: 'Your password has been reset', show: true });
    } else if (messageParams === 'forgot-password-success') {
      setShowToast({
        ...showToast,
        message: 'Password reset instructions sent to your email if it has already been registered.',
        show: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageParams]);

  return (
    <>
      <FeatureFlagProvider>
        <ErrorBoundary>
          <ModalRoot open onOpenChange={onOpenChangeModal}>
            <ModalPortal>
              <ModalOverlay />
              <ModalContent className="w-[440px]">{children}</ModalContent>
            </ModalPortal>
          </ModalRoot>
        </ErrorBoundary>
        <Toast
          closeText="OK"
          description={showToast.message}
          duration={3000}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop,
          onOpenChange={(isOpen: boolean): void => {
            setShowToast({ ...showToast, show: isOpen });
          }}
          open={showToast.show}
        />
      </FeatureFlagProvider>
    </>
  );
}
